import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Briefcase, Image, MessageSquare, FileText, HelpCircle, Settings, Calendar, Search } from "lucide-react";
import ManageServices from "@/components/admin/ManageServices";
import ManageGallery from "@/components/admin/ManageGallery";
import ManageTestimonials from "@/components/admin/ManageTestimonials";
import ManageBlog from "@/components/admin/ManageBlog";
import ManageFAQs from "@/components/admin/ManageFAQs";
import ManageSettings from "@/components/admin/ManageSettings";
import ManageSEO from "@/components/admin/ManageSEO";
import ViewBookings from "@/components/admin/ViewBookings";
import BookingCalendar from "@/components/admin/BookingCalendar";
import AdminOverview from "@/components/admin/AdminOverview";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUsername");
    navigate("/admin/login");
  };

  const menuItems = [
    { path: "/admin/dashboard", label: "Overview", icon: LayoutDashboard },
    { path: "/admin/services", label: "Services", icon: Briefcase },
    { path: "/admin/gallery", label: "Gallery", icon: Image },
    { path: "/admin/testimonials", label: "Testimonials", icon: MessageSquare },
    { path: "/admin/blog", label: "Blog", icon: FileText },
    { path: "/admin/faqs", label: "FAQs", icon: HelpCircle },
    { path: "/admin/bookings", label: "All Bookings", icon: Calendar },
    { path: "/admin/calendar", label: "Calendar", icon: Calendar },
    { path: "/admin/seo", label: "SEO", icon: Search },
    { path: "/admin/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-lg fixed h-full z-10" data-testid="admin-sidebar">
        <div className="p-6 border-b">
          <div className="flex items-center mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bartender-hub-3/artifacts/jjg0m534_Melange%20Logo%20New.png" 
              alt="Mélange" 
              className="h-10 w-auto mr-3"
            />
          </div>
          <p className="text-sm text-gray-600 mt-1">Management Portal</p>
        </div>
        <nav className="p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`admin-nav-${item.label.toLowerCase()}`}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? "bg-[#F0EBE3] text-[#8B7355] font-medium"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <Icon size={20} className="mr-3" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full"
            data-testid="admin-logout-button"
          >
            <LogOut size={20} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 flex-1 p-8">
        <Routes>
          <Route path="dashboard" element={<AdminOverview />} />
          <Route path="services" element={<ManageServices />} />
          <Route path="gallery" element={<ManageGallery />} />
          <Route path="testimonials" element={<ManageTestimonials />} />
          <Route path="blog" element={<ManageBlog />} />
          <Route path="faqs" element={<ManageFAQs />} />
          <Route path="bookings" element={<ViewBookings />} />
          <Route path="calendar" element={<BookingCalendar />} />
          <Route path="seo" element={<ManageSEO />} />
          <Route path="settings" element={<ManageSettings />} />
          <Route path="/" element={<AdminOverview />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
